import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getProfiles, getUserStats, getNotifications, updateUserProfile } from '../lib/api';
import { Target, Trophy, TrendingUp, Bell, LogOut, Settings, Crosshair, Shield, History, ChevronRight, Newspaper, Briefcase } from 'lucide-react';
import Marquee from 'react-fast-marquee';
import { playButtonSound, playFlipperSound } from '../lib/sounds';

export default function DashboardPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [activeRole, setActiveRole] = useState(user?.player_role || 'presse');

  const loadData = useCallback(async (role) => {
    const r = role || activeRole;
    try {
      const results = await Promise.allSettled([
        getProfiles(r),
        getUserStats(),
        getNotifications()
      ]);
      if (results[0].status === 'fulfilled') setProfiles(results[0].value.data);
      if (results[1].status === 'fulfilled') setStats(results[1].value.data);
      if (results[2].status === 'fulfilled') setNotifications(results[2].value.data);
    } catch (e) {
      console.error(e);
    }
  }, [activeRole]);

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const switchRole = async (role) => {
    if (role === activeRole) return;
    try { playButtonSound(); } catch {}
    setActiveRole(role);
    // Update in DB
    updateUserProfile({ player_role: role }).catch(() => {});
    refreshUser().catch(() => {});
    // Reload profiles for new role
    try {
      const { data } = await getProfiles(role);
      setProfiles(data);
    } catch {}
  };

  const getLevelTitle = (level) => {
    const titles = ['Rookie', 'Amateur', 'Semi-Pro', 'Pro', 'Expert', 'Master', 'Champion', 'Legend', 'Titan', 'GOD'];
    return titles[Math.min(level - 1, 9)] || 'Rookie';
  };

  const getDifficultyStars = (d) => '★'.repeat(d) + '☆'.repeat(4 - d);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Top Marquee */}
      <div className="bg-[#121214] border-b-2 border-[#E61C24]/30 py-1">
        <Marquee speed={40} gradient={false}>
          <span className="font-mono text-[#FFF200] text-sm mx-8">
            90 SECONDES POUR CONVAINCRE &bull; ENTRAINEZ-VOUS &bull; PROGRESSEZ &bull; DEVENEZ EXPERT &bull;
          </span>
        </Marquee>
      </div>

      {/* Header */}
      <div className="bg-[#121214] border-b-2 border-[#00AEEF]/30 p-4 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl uppercase tracking-wider text-[#E61C24] drop-shadow-[0_0_8px_rgba(230,28,36,0.6)]">
            MISSION CONTROL
          </h1>
          <p className="font-body text-[#A1A1AA] text-sm">{user?.name || 'Player'}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="notifications-btn"
            onClick={() => setShowNotifs(!showNotifs)}
            className="relative p-2 text-[#00AEEF] hover:text-[#33C3FF] transition-colors"
          >
            <Bell size={22} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#E61C24] text-white text-xs font-mono w-5 h-5 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            data-testid="history-nav-btn"
            onClick={() => navigate('/history')}
            className="p-2 text-[#00AEEF] hover:text-[#33C3FF] transition-colors"
          >
            <History size={22} />
          </button>
          <button
            data-testid="leaderboard-nav-btn"
            onClick={() => navigate('/leaderboard')}
            className="p-2 text-[#FFF200] hover:text-yellow-300 transition-colors"
          >
            <Trophy size={22} />
          </button>
          {user?.role === 'admin' && (
            <button
              data-testid="admin-nav-btn"
              onClick={() => navigate('/admin')}
              className="p-2 text-[#39FF14] hover:text-green-300 transition-colors"
            >
              <Shield size={22} />
            </button>
          )}
          <button
            data-testid="profile-nav-btn"
            onClick={() => navigate('/profile')}
            className="p-2 text-[#A1A1AA] hover:text-white transition-colors"
          >
            <Settings size={22} />
          </button>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="p-2 text-[#A1A1AA] hover:text-[#E61C24] transition-colors"
          >
            <LogOut size={22} />
          </button>
        </div>
      </div>

      {/* Notification dropdown */}
      {showNotifs && (
        <div className="bg-[#121214] border-2 border-[#00AEEF]/30 mx-4 mt-2 p-4 max-h-60 overflow-auto">
          <h3 className="font-heading text-sm uppercase text-[#00AEEF] mb-2">Notifications</h3>
          {notifications.length === 0 ? (
            <p className="font-mono text-[#71717A] text-sm">Aucune notification</p>
          ) : (
            notifications.map((n, i) => (
              <div key={i} className={`font-mono text-sm p-2 border-b border-gray-800 ${n.read ? 'text-[#71717A]' : 'text-[#FFF200]'}`}>
                {n.message}
              </div>
            ))
          )}
        </div>
      )}

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Stats Panel */}
        <div className="bg-[#121214] border-2 border-[#E61C24]/50 shadow-[inset_0_0_20px_rgba(230,28,36,0.15)] p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="text-[#39FF14] drop-shadow-[0_0_6px_rgba(57,255,20,0.6)]" size={20} />
            <h2 className="font-heading text-lg uppercase tracking-wider">Stats</h2>
          </div>
          {stats ? (
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-black border border-gray-800 p-3 text-center">
                <div className="font-mono text-3xl text-[#FF333B] drop-shadow-[0_0_10px_rgba(255,51,59,0.6)]">
                  {stats.best_score}
                </div>
                <div className="font-mono text-xs text-[#A1A1AA] mt-1">BEST</div>
              </div>
              <div className="bg-black border border-gray-800 p-3 text-center">
                <div className="font-mono text-3xl text-[#33C3FF] drop-shadow-[0_0_10px_rgba(0,174,239,0.6)]">
                  {stats.avg_score}
                </div>
                <div className="font-mono text-xs text-[#A1A1AA] mt-1">AVG</div>
              </div>
              <div className="bg-black border border-gray-800 p-3 text-center">
                <div className="font-mono text-3xl text-[#FFF200] drop-shadow-[0_0_10px_rgba(255,242,0,0.6)]">
                  {stats.total_sessions}
                </div>
                <div className="font-mono text-xs text-[#A1A1AA] mt-1">GAMES</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-black border border-gray-800 p-3 text-center">
                <div className="font-mono text-3xl text-[#FF333B]">0</div>
                <div className="font-mono text-xs text-[#A1A1AA] mt-1">BEST</div>
              </div>
              <div className="bg-black border border-gray-800 p-3 text-center">
                <div className="font-mono text-3xl text-[#33C3FF]">0</div>
                <div className="font-mono text-xs text-[#A1A1AA] mt-1">AVG</div>
              </div>
              <div className="bg-black border border-gray-800 p-3 text-center">
                <div className="font-mono text-3xl text-[#FFF200]">0</div>
                <div className="font-mono text-xs text-[#A1A1AA] mt-1">GAMES</div>
              </div>
            </div>
          )}
          <div className="mt-3 flex items-center justify-between">
            <span className="font-mono text-sm text-[#A1A1AA]">
              LEVEL {stats?.level || user?.level || 1} - {getLevelTitle(stats?.level || user?.level || 1)}
            </span>
            <div className="w-32 h-2 bg-black border border-gray-700">
              <div
                className="h-full bg-[#39FF14] transition-all"
                style={{ width: `${Math.min(100, ((stats?.total_sessions || 0) % 5) * 20)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Role Selector */}
        <div className="flex gap-2">
          <button
            data-testid="role-presse-btn"
            onClick={() => switchRole('presse')}
            className={`flex-1 py-3 flex items-center justify-center gap-2 border-2 font-heading text-sm uppercase tracking-wider transition-all ${
              activeRole === 'presse'
                ? 'bg-[#00AEEF] text-black border-[#00AEEF] shadow-[0_0_15px_rgba(0,174,239,0.5)]'
                : 'bg-transparent text-[#00AEEF]/50 border-[#00AEEF]/20 hover:border-[#00AEEF]/50'
            }`}
          >
            <Newspaper size={16} />
            Presse
          </button>
          <button
            data-testid="role-commercial-btn"
            onClick={() => switchRole('commercial')}
            className={`flex-1 py-3 flex items-center justify-center gap-2 border-2 font-heading text-sm uppercase tracking-wider transition-all ${
              activeRole === 'commercial'
                ? 'bg-[#E61C24] text-white border-[#E61C24] shadow-[0_0_15px_rgba(230,28,36,0.5)]'
                : 'bg-transparent text-[#E61C24]/50 border-[#E61C24]/20 hover:border-[#E61C24]/50'
            }`}
          >
            <Briefcase size={16} />
            Commercial
          </button>
        </div>

        {/* Target Selection */}
        <div className="bg-[#121214] border-2 border-[#00AEEF]/50 shadow-[inset_0_0_20px_rgba(0,174,239,0.15)] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Crosshair className="text-[#E61C24] drop-shadow-[0_0_6px_rgba(230,28,36,0.6)]" size={20} />
            <h2 className="font-heading text-lg uppercase tracking-wider">Choisir Cible</h2>
          </div>

          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
            {profiles.length === 0 ? (
              <div className="text-center py-6">
                <p className="font-mono text-[#71717A] mb-3">Aucun profil chargé</p>
                <button
                  data-testid="reload-profiles-btn"
                  onClick={loadData}
                  className="font-heading text-sm uppercase text-[#00AEEF] border border-[#00AEEF] px-4 py-2 hover:bg-[#00AEEF] hover:text-black transition-all"
                >
                  RECHARGER
                </button>
              </div>
            ) : profiles.map((profile) => (
              <button
                key={profile.id}
                data-testid={`profile-${profile.id}`}
                onClick={() => { playFlipperSound(); navigate(`/simulation?profile=${profile.id}`); }}
                className="w-full text-left bg-black border border-gray-800 hover:border-[#E61C24]/50 p-3 transition-all hover:shadow-[0_0_10px_rgba(230,28,36,0.2)] group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Target className="text-[#E61C24] group-hover:text-[#FF333B] transition-colors" size={16} />
                      <span className="font-heading text-sm uppercase tracking-wide text-white group-hover:text-[#FF333B] transition-colors">
                        {profile.name}
                      </span>
                    </div>
                    <p className="font-body text-xs text-[#71717A] mt-1 line-clamp-1">{profile.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-mono text-xs text-[#FFF200]">{getDifficultyStars(profile.difficulty)}</span>
                      <span className="bg-[#E61C24]/20 border border-[#E61C24]/50 text-[#FF333B] font-mono text-xs px-2 py-0.5 uppercase">
                        {profile.tone}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="text-[#71717A] group-hover:text-[#E61C24] transition-colors" size={20} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
